import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, PackagePlus, Check, Hourglass } from "lucide-react";

const BADGES = {
  disponivel: ["Disponível", "border-zinc-700 text-zinc-300 bg-zinc-900"],
  solicitado: ["Solicitado", "border-sky-700 text-sky-200 bg-sky-950/40"],
  ativo: ["Ativo", "border-green-700 text-green-200 bg-green-950/40"],
};

export default function OwnerAddons() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const load = async () => { try { const { data } = await api.get("/addons"); setRows(data); } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const request = async (a) => {
    setActing(a.id);
    try { await api.post(`/addons/${a.id}/request`); toast.success("Pedido enviado ao suporte"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setActing(null); }
  };
  return (
    <div data-testid="owner-addons" className="space-y-5 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Configurações</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Pacotes adicionais</h1>
        <p className="text-sm text-zinc-400 mt-1">Recursos extras que você pode contratar para a sua oficina.</p>
      </div>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-green-brand" /> : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-10 text-center">
          <PackagePlus className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400">Nenhum pacote disponível no momento.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((a) => {
            const [label, cls] = BADGES[a.status] || BADGES.disponivel;
            return (
              <div key={a.id} data-testid={`addon-${a.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg font-bold text-zinc-100">{a.name}</div>
                    <p className="text-xs text-zinc-400 mt-0.5">{a.description || "Sem descrição."}</p>
                  </div>
                  <span data-testid={`addon-${a.id}-status`} className={`inline-flex items-center h-6 px-2 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{label}</span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-zinc-800">
                  <div className="text-sm text-zinc-200">
                    <div><strong className="font-display text-base">{brl(a.price)}</strong> <span className="text-[11px] text-zinc-500">/ mês</span></div>
                    {a.limit && <div className="text-[11px] text-zinc-500">Limite: {a.limit}{a.status === "ativo" && typeof a.used === "number" ? ` · usado ${a.used}` : ""}</div>}
                  </div>
                  {a.status === "disponivel" && (
                    <Button data-testid={`addon-${a.id}-request`} onClick={() => request(a)} disabled={acting === a.id} className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold">
                      {acting === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Contratar"}
                    </Button>
                  )}
                  {a.status === "solicitado" && <span className="inline-flex items-center gap-1 text-[11px] text-sky-300 font-bold"><Hourglass className="h-3.5 w-3.5" /> Aguardando aprovação</span>}
                  {a.status === "ativo" && <span className="inline-flex items-center gap-1 text-[11px] text-green-300 font-bold"><Check className="h-3.5 w-3.5" /> Ativo</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
